import { GameDocument, MapImage } from '../../../types/game-document';
import { ResourceEntity, ZoneEntity } from '../../../types/game-document/';
import { uuid } from '../../../types/common-helper';
import {
  DeleteAreaAsync,
  DeleteMapAsync,
  GetNextAssetNameAsync,
  MergeAssets
} from './index';
import {
  CopyResourceAsync,
  DeleteResourceAsync,
  ImportResourceAsync
} from '../resources';
import { FilterResourcePacks } from '../resource-packs';
import { ExportedZoneData } from '../../../types/game-document/entities/exported-zone';
import _ from 'lodash';
import { Zone } from '../../../types/game-document/entities/zone';
import type { Map as MapEntity } from '../../../types/game-document/entities/map';
import {
  AreaEntity,
  TaskEntity,
  TimerEntity
} from '../../../types/game-document/';
import { GetResourcesWithResId as GetTaskContentResources } from './task-contents';
import { GetItemResources } from './items';
import { GetTitleResourcesWithResId } from './titles';
import {
  Algorithm,
  AlgorithmControlStructure,
  AlgorithmStep
} from '../../../types/algorithm';

/**
 * Adds a new Zone to the Game document.
 * @param gameDocument - The Game Document to modify
 * @param name - The Name of the new Zone
 * @param description - The Description for the new Zone
 * @returns The updated Game Document
 */
export const AddZoneAsync = async (
  gameDocument: GameDocument,
  name: string,
  description: string,
  titleResId: string
) => {
  let zones = gameDocument.assets.zones ?? [];
  let zoneName = await GetNextAssetNameAsync(zones, name);

  zones.push({
    id: uuid(),
    name: zoneName,
    description,
    titleResId,
    isVisible: true
  });
  return MergeAssets(gameDocument, zones, 'zones');
};

/**
 * Add Zone.
 * @param gameDocument - The Game Document to modify
 * @param tasks - The new zone
 * @return The updated Game Document
 */
export const AddZoneEntityAsync = async (
  gameDocument: GameDocument,
  zone: ZoneEntity
) => {
  let zones = gameDocument.assets.zones ?? [];
  let zoneName = await GetNextAssetNameAsync(zones, zone?.name);
  zone.name = zoneName;
  zones.push(zone);

  return MergeAssets(gameDocument, zones, 'zones');
};

/**
 * Deletes the identified Zone from the Game Document.
 * @param gameDocument - The Game Document to modify
 * @param zoneId - The ID of the Zone to delete
 * @returns The updated Game Document
 */
export const DeleteZoneAsync = async (
  gameDocument: GameDocument,
  zoneId: string
) => {
  const zones = gameDocument.assets.zones ?? [];
  const zoneIndex = zones.findIndex((i) => i.id === zoneId)!;

  //Delete map from map assets
  const mapAsset = gameDocument?.rules?.worldMap?.zones?.find(
    (x) => x.zoneAssId === zoneId
  );

  if (mapAsset && mapAsset.mapAssId) {
    await DeleteMapAsync(gameDocument, mapAsset.mapAssId);

    //Delete zone in rules zone
    const zoneIndexInRules = gameDocument.rules.worldMap.zones.findIndex(
      (z) => z.mapAssId === mapAsset.mapAssId
    );

    if (zoneIndexInRules !== -1)
      gameDocument.rules.worldMap.zones.splice(zoneIndexInRules, 1);
    //End delete zone in rules zone
  }
  //End delete map from map assets

  if (zoneIndex !== -1) {
    //Delete all task boundary in deleted zone
    const tasks = gameDocument.assets.tasks!;
    const tasksZone = zones[zoneIndex].tasks?.map((e) => e.taskAssId);

    tasksZone?.forEach((id) => {
      const taskIndex = tasks?.findIndex((task) => task.id === id);
      if (taskIndex !== undefined && taskIndex !== -1) {
        delete tasks[taskIndex].boundary;
      }
    });
    //End delete all task boundary in deleted zone

    //Delete all areas in deleted zone
    const areas = zones[zoneIndex].areas ?? [];
    if (areas.length > 0) {
      await Promise.all(
        areas.map(({ areaAssId }) => DeleteAreaAsync(gameDocument, areaAssId))
      );
    }
    //End delete all areas in deleted zone

    await DeleteResourceAsync(gameDocument, zones[zoneIndex].titleResId!);
    zones.splice(zoneIndex, 1);
  }

  return MergeAssets(gameDocument, zones, 'zones');
};

/**
 * Updates the identified Zone in the Game Document.
 * @param gameDocument - The Game Document to modify
 * @param zoneId - The ID of the Zone to update
 * @param zone - The updated Zone
 * @constructor
 */
export const UpdateZoneAsync = async (
  gameDocument: GameDocument,
  zoneId: string,
  zone: ZoneEntity
) => {
  let zones = gameDocument.assets.zones ?? [];
  let zoneIndex = zones.findIndex((i) => i.id === zoneId)!;
  zone.name = await GetNextAssetNameAsync(zones, zone.name, zone.id);
  zones[zoneIndex] = zone;

  return MergeAssets(gameDocument, zones, 'zones');
};

export const UpdateZone = (
  gameDocument: GameDocument,
  zoneId: string,
  zone: ZoneEntity
) => {
  let zones = gameDocument.assets.zones ?? [];
  let zoneIndex = zones.findIndex((i) => i.id === zoneId)!;
  GetNextAssetNameAsync(zones, zone.name, zone.id)
    .then((nextName: string) => {
      zone.name = nextName;
      zones[zoneIndex] = zone;
      return MergeAssets(gameDocument, zones, 'zones');
    })
    .catch((error: any) => {
      // Handle errors here
      console.error('Error:', error);
    });

  zones[zoneIndex] = zone;

  return MergeAssets(gameDocument, zones, 'zones');
};

/**
 * Create a copy of the Zone in the Game document.
 * @param gameDocument - The Game Document to modify
 * @param zoneId - The ID of the Zone to copy
 * @param copiedZoneId - The zone id to copy but different
 * @returns The updated Game Document
 */
export const CopyZoneAsync = async (
  gameDocument: GameDocument,
  zoneId: string,
  copiedZoneId: string = uuid()
) => {
  let zones = gameDocument.assets.zones ?? [];
  let zoneIndex = zones.findIndex((i) => i.id === zoneId)!;
  if (zoneIndex !== -1) {
    let zoneCopy: ZoneEntity = {
      ...zones[zoneIndex],
      titleResId: uuid()
    };
    await CopyResourceAsync(
      gameDocument,
      zones[zoneIndex].titleResId!,
      zoneCopy.titleResId
    );

    zoneCopy.id = copiedZoneId;
    zoneCopy.name += '-copy';
    zoneCopy.name = await GetNextAssetNameAsync(
      zones,
      zoneCopy.name,
      zoneCopy.id
    );

    zones.push(zoneCopy);
  }
  return MergeAssets(gameDocument, zones, 'zones');
};

/**
 * Get all zones from game document local storage.
 * @param gameDocument - The Game Document as datasource
 * @constructor
 */
export const GetZones = (gameDocument: GameDocument | undefined) => {
  return gameDocument?.assets.zones ?? [];
};

/**
 * Get zones by zone id from game document local storage.
 * @param gameDocument - The Game Document as datasource
 * @param zoneId - The zone id you are looking for
 * @constructor
 */
export const GetZoneById = (
  gameDocument: GameDocument | undefined,
  zoneId: string
) => {
  return gameDocument?.assets?.zones?.find((x) => x.id === zoneId);
};

/**
 * Get zones by map id from game document local storage.
 * @param gameDocument - The Game Document as datasource
 * @param mapId - The map id you are looking for
 * @constructor
 */
export const GetZoneByMapId = (
  gameDocument: GameDocument | undefined,
  mapId: string
) => {
  return gameDocument?.rules?.worldMap?.zones.find((x) => x.mapAssId === mapId);
};

/**
 * Get zones by Ids from game document local storage.
 * @param gameDocument - The Game Document as datasource
 * @constructor
 */
export const GetZonesByIds = (gameDocument: GameDocument, ids: string[]) => {
  return (
    gameDocument?.assets?.zones?.filter((x) => ids.indexOf(x.id) !== -1) ?? []
  );
};

/**
 * Clear the argumentAssetId for all navigateToZone steps in an algorithm.
 * This ensures exported zone data does not pull in cross-zone dependencies
 * while still maintaining the step in the algorithm.
 */
const ClearNavigateToZoneArgs = (
  algorithm?: Algorithm
): Algorithm | undefined => {
  if (!algorithm?.steps) return algorithm;

  const cloned = _.cloneDeep(algorithm);

  const processSteps = (
    steps: Array<AlgorithmStep | AlgorithmControlStructure>
  ): Array<AlgorithmStep | AlgorithmControlStructure> => {
    return steps.map((step) => {
      const op = (step.operation || '').toString().trim().toLowerCase();
      if (op === 'navigatetozone') {
        step.argumentAssetId = '';
      }
      const cs = step as AlgorithmControlStructure;
      if (cs.ifSteps) cs.ifSteps = processSteps(cs.ifSteps);
      if (cs.elseSteps) cs.elseSteps = processSteps(cs.elseSteps);
      return step;
    });
  };

  cloned.steps = processSteps(cloned.steps!);
  return cloned;
};

/**
 * Export the zones including the related resources.
 * @param gameDocument - The Game Document as datasource
 * @param selectedIds - The list of Zone Id that will be exported
 */
export const ExportZones = (
  gameDocument: GameDocument,
  selectedIds: string[]
): ExportedZoneData => {
  const taskIds = new Set<string>();
  const areaIds = new Set<string>();
  const timerIds = new Set<string>();
  const itemIds = new Set<string>();
  const titleIds = new Set<string>();
  const zoneIds = new Set<string>(selectedIds);
  const mapIds = new Set<string>();

  const assetIds = {
    items: itemIds,
    titles: titleIds,
    tasks: taskIds,
    areas: areaIds,
    timers: timerIds,
    zones: zoneIds
  };

  // Recursive collection loop
  let previousSize = -1;
  while (true) {
    const currentSize =
      zoneIds.size +
      taskIds.size +
      areaIds.size +
      timerIds.size +
      itemIds.size +
      titleIds.size +
      mapIds.size;

    if (currentSize === previousSize) break;
    previousSize = currentSize;

    // 1. Process World Map Rules (Zone-Timer-Map links only)
    // Note: World map events (onEnterMap, onExitMap, onTaskCompletedMap) are NOT
    // processed here because they belong to the world map, not to individual zones.
    // Including them would incorrectly pull in unrelated assets.

    const worldMapZonesInExport =
      gameDocument.rules?.worldMap?.zones?.filter((z) =>
        zoneIds.has(z.zoneAssId)
      ) || [];
    worldMapZonesInExport.forEach((wmz) => {
      if (wmz.timerAssId) timerIds.add(wmz.timerAssId);
      if (wmz.mapAssId) mapIds.add(wmz.mapAssId);
    });

    // 2. Process Zones
    const currentZones =
      gameDocument.assets?.zones?.filter((z) => zoneIds.has(z.id)) || [];
    currentZones.forEach((zone) => {
      zone.tasks?.forEach((t) => taskIds.add(t.taskAssId));
      zone.areas?.forEach((a) => areaIds.add(a.areaAssId));
      zone.timers?.forEach((t) => timerIds.add(t.timerAssId));

      CollectAssetIdsFromAlgorithm(zone.events?.onEnterMap, assetIds);
      CollectAssetIdsFromAlgorithm(zone.events?.onExitMap, assetIds);
      CollectAssetIdsFromAlgorithm(zone.events?.onTaskCompletedMap, assetIds);
    });

    // 3. Process Tasks
    const currentTasks =
      gameDocument.assets?.tasks?.filter((t) => taskIds.has(t.id)) || [];
    currentTasks.forEach((t) => {
      if (t.timerAssId) timerIds.add(t.timerAssId);
      CollectAssetIdsFromAlgorithm(t.events?.onOpenTask, assetIds);
      CollectAssetIdsFromAlgorithm(t.events?.onCompleteTask, assetIds);
      CollectAssetIdsFromAlgorithm(t.events?.onCloseTask, assetIds);
      CollectAssetIdsFromAlgorithm(t.events?.onProximity, assetIds);
    });

    // 4. Process Areas
    const currentAreas =
      gameDocument.assets?.areas?.filter((a) => areaIds.has(a.id)) || [];
    currentAreas.forEach((a) => {
      CollectAssetIdsFromAlgorithm(a.events?.onEntry, assetIds);
      CollectAssetIdsFromAlgorithm(a.events?.onExit, assetIds);
    });

    // 5. Process Timers
    const currentTimers =
      gameDocument.assets?.timers?.filter((t) => timerIds.has(t.id)) || [];
    currentTimers.forEach((t) => {
      CollectAssetIdsFromAlgorithm(t.events?.onStartTimer, assetIds);
      CollectAssetIdsFromAlgorithm(t.events?.onStopTimer, assetIds);
      CollectAssetIdsFromAlgorithm(t.events?.onElapsedTimer, assetIds);
    });
  }

  // Final list of World Map Zones for export metadata
  const worldMapZones =
    gameDocument.rules?.worldMap?.zones?.filter((item) =>
      zoneIds.has(item.zoneAssId)
    ) || [];

  // Fetch entities
  const finalZones =
    gameDocument.assets?.zones?.filter((z) => zoneIds.has(z.id)) || [];
  const finalMaps =
    gameDocument.assets?.maps?.filter((m) => mapIds.has(m.id)) || [];
  const finalTasks =
    gameDocument.assets?.tasks?.filter((t) => taskIds.has(t.id)) || [];
  const finalAreas =
    gameDocument.assets?.areas?.filter((a) => areaIds.has(a.id)) || [];
  const finalTimers =
    gameDocument.assets?.timers?.filter((t) => timerIds.has(t.id)) || [];
  const finalItems =
    gameDocument.assets?.items?.filter((i) => itemIds.has(i.id)) || [];
  const finalTitles =
    gameDocument.assets?.titles?.filter((t) => titleIds.has(t.id)) || [];
  const finalMapImages =
    gameDocument.assets?.mapImages?.filter((mi) => mapIds.has(mi.mapId)) || [];

  const taskContentIds = new Set<string>();
  finalTasks.forEach((t) => {
    if (t.taskContentId) taskContentIds.add(t.taskContentId);
  });

  const finalTaskContents =
    gameDocument.assets?.taskContents?.filter((tc) =>
      taskContentIds.has(tc.id)
    ) || [];

  const currentResources = gameDocument.resources;
  let relatedResources: ResourceEntity[] = [];

  finalZones.forEach((item) => {
    relatedResources = _.concat(
      relatedResources,
      GetZoneResourcesWithResId(item, currentResources)
    );
  });
  finalMaps.forEach((item) => {
    relatedResources = _.concat(
      relatedResources,
      GetZoneMapResourcesWithResId(item, currentResources)
    );
  });
  finalTasks.forEach((item) => {
    relatedResources = _.concat(
      relatedResources,
      GetTaskResourcesWithResId(item, currentResources)
    );
  });
  finalAreas.forEach((item) => {
    relatedResources = _.concat(
      relatedResources,
      GetAreaResourcesWithResId(item, currentResources)
    );
  });
  finalTimers.forEach((item) => {
    relatedResources = _.concat(
      relatedResources,
      GetTimerResourcesWithResId(item, currentResources)
    );
  });
  finalTaskContents.forEach((item) => {
    relatedResources = _.concat(
      relatedResources,
      GetTaskContentResources(item, currentResources)
    );
  });
  finalItems.forEach((item) => {
    relatedResources = _.concat(
      relatedResources,
      GetItemResources(item, currentResources)
    );
  });
  finalTitles.forEach((item) => {
    relatedResources = _.concat(
      relatedResources,
      GetTitleResourcesWithResId(item, currentResources)
    );
  });
  finalMapImages.forEach((item) => {
    relatedResources = _.concat(
      relatedResources,
      GetMapImageResources(item, currentResources)
    );
  });

  relatedResources = _.uniqBy(relatedResources, 'id');
  const resourcePacks = FilterResourcePacks(gameDocument, relatedResources);

  // Clear navigateToZone arguments from all exported algorithms
  const clearedZones = finalZones.map((z) => {
    const clone = _.cloneDeep(z);
    if (clone.events) {
      clone.events.onEnterMap = ClearNavigateToZoneArgs(
        clone.events.onEnterMap
      );
      clone.events.onExitMap = ClearNavigateToZoneArgs(clone.events.onExitMap);
      clone.events.onTaskCompletedMap = ClearNavigateToZoneArgs(
        clone.events.onTaskCompletedMap
      );
    }
    return clone;
  });
  const clearedTasks = finalTasks.map((t) => {
    const clone = _.cloneDeep(t);
    if (clone.events) {
      clone.events.onOpenTask = ClearNavigateToZoneArgs(
        clone.events.onOpenTask
      );
      clone.events.onCompleteTask = ClearNavigateToZoneArgs(
        clone.events.onCompleteTask
      );
      clone.events.onCloseTask = ClearNavigateToZoneArgs(
        clone.events.onCloseTask
      );
      clone.events.onProximity = ClearNavigateToZoneArgs(
        clone.events.onProximity
      );
    }
    return clone;
  });
  const clearedAreas = finalAreas.map((a) => {
    const clone = _.cloneDeep(a);
    if (clone.events) {
      clone.events.onEntry = ClearNavigateToZoneArgs(clone.events.onEntry);
      clone.events.onExit = ClearNavigateToZoneArgs(clone.events.onExit);
    }
    return clone;
  });
  const clearedTimers = finalTimers.map((t) => {
    const clone = _.cloneDeep(t);
    if (clone.events) {
      clone.events.onStartTimer = ClearNavigateToZoneArgs(
        clone.events.onStartTimer
      );
      clone.events.onStopTimer = ClearNavigateToZoneArgs(
        clone.events.onStopTimer
      );
      clone.events.onElapsedTimer = ClearNavigateToZoneArgs(
        clone.events.onElapsedTimer
      );
    }
    return clone;
  });

  return {
    worldMapZones,
    maps: finalMaps,
    zones: clearedZones,
    tasks: clearedTasks,
    areas: clearedAreas,
    timers: clearedTimers,
    taskContents: finalTaskContents,
    items: finalItems,
    titles: finalTitles,
    mapImages: finalMapImages,
    resources: relatedResources,
    resourcePacks
  };
};

export const GetZoneResourcesWithResId = (
  zone: Zone,
  resources: ResourceEntity[]
): ResourceEntity[] => {
  const resIds: string[] = [];

  if (zone.titleResId) resIds.push(zone.titleResId);
  if (zone.imageResId) resIds.push(zone.imageResId);

  return _.filter(resources, (resource) => _.includes(resIds, resource.id));
};

export const GetZoneMapResourcesWithResId = (
  map: MapEntity,
  resources: ResourceEntity[]
): ResourceEntity[] => {
  const resIds: string[] = [];

  if (map.titleResId) resIds.push(map.titleResId);
  if (map.imageResId) resIds.push(map.imageResId);

  return _.filter(resources, (resource) => _.includes(resIds, resource.id));
};

export const GetTaskResourcesWithResId = (
  task: TaskEntity,
  resources: ResourceEntity[]
): ResourceEntity[] => {
  const resIds: string[] = [];

  if (task.titleResId) resIds.push(task.titleResId);
  if (task.imageResId) resIds.push(task.imageResId);
  if (task.completeImageResId) resIds.push(task.completeImageResId);
  if (task.pin?.imageResId) resIds.push(task.pin.imageResId);

  return _.filter(resources, (resource) => _.includes(resIds, resource.id));
};

export const GetAreaResourcesWithResId = (
  area: AreaEntity,
  resources: ResourceEntity[]
): ResourceEntity[] => {
  const resIds: string[] = [];

  if (area.titleResId) resIds.push(area.titleResId);
  if (area.imageResId) resIds.push(area.imageResId);

  return _.filter(resources, (resource) => _.includes(resIds, resource.id));
};

export const GetTimerResourcesWithResId = (
  timer: TimerEntity,
  resources: ResourceEntity[]
): ResourceEntity[] => {
  const resIds: string[] = [];

  if (timer.titleResId) resIds.push(timer.titleResId);

  return _.filter(resources, (resource) => _.includes(resIds, resource.id));
};

export const GetMapImageResources = (
  mapImage: MapImage,
  resources: ResourceEntity[]
): ResourceEntity[] => {
  const resIds: string[] = [];

  if (mapImage.titleResId) resIds.push(mapImage.titleResId);
  if (mapImage.imageResId) resIds.push(mapImage.imageResId);

  return _.filter(resources, (resource) => _.includes(resIds, resource.id));
};

export const CollectAssetIdsFromAlgorithm = (
  algorithm?: Algorithm,
  assetIds: {
    items: Set<string>;
    titles: Set<string>;
    tasks: Set<string>;
    areas: Set<string>;
    timers: Set<string>;
    zones: Set<string>;
  } = {
    items: new Set(),
    titles: new Set(),
    tasks: new Set(),
    areas: new Set(),
    timers: new Set(),
    zones: new Set()
  }
) => {
  if (!algorithm?.steps) return assetIds;

  const taskActions = [
    'opentask',
    'showtask',
    'hidetask',
    'randomtask',
    'distributetask',
    'resettask',
    'openrandomtask',
    'completetask'
  ];
  const itemActions = [
    'giveitem',
    'removeitem',
    'addbankstock',
    'removebankstock',
    'haveitem'
  ];
  const titleActions = ['givetitle', 'removetitle', 'haverole', 'assignTitle'];
  const areaActions = ['showarea', 'hidearea'];
  const timerActions = ['starttimer', 'stoptimer', 'pausetimer', 'resumetimer'];

  const traverseSteps = (
    steps: Array<AlgorithmStep | AlgorithmControlStructure>
  ) => {
    steps.forEach((step) => {
      // The operation name (e.g. giveItem, removeTitle) is stored in step.operation.
      // step.action holds the event context (e.g. Task.onComplete), not the operation name.
      const actionStr = (step.operation || '').toString().trim().toLowerCase();
      const assetId = step.argumentAssetId;

      if (assetId && actionStr) {
        const ids = Array.isArray(assetId) ? assetId : [assetId];
        ids.forEach((id) => {
          if (typeof id !== 'string') return; // Ensure valid string ID

          if (taskActions.includes(actionStr)) assetIds.tasks.add(id);
          else if (itemActions.includes(actionStr)) assetIds.items.add(id);
          else if (titleActions.includes(actionStr)) assetIds.titles.add(id);
          else if (areaActions.includes(actionStr)) assetIds.areas.add(id);
          else if (timerActions.includes(actionStr)) assetIds.timers.add(id);
        });
      }

      const controlStructure = step as AlgorithmControlStructure;
      if (controlStructure.ifSteps) {
        traverseSteps(controlStructure.ifSteps);
      }
      if (controlStructure.elseSteps) {
        traverseSteps(controlStructure.elseSteps);
      }

      // Collect from compound statements (e.g. haveItem conditions)
      const collectFromCompound = (
        statements?: Array<{
          operation?: string;
          argumentAssetId?: string | string[];
        }>
      ) => {
        statements?.forEach((cs) => {
          const csOp = (cs.operation || '').toString().trim().toLowerCase();
          const csAssetId = cs.argumentAssetId;
          if (csAssetId && csOp) {
            const csIds = Array.isArray(csAssetId) ? csAssetId : [csAssetId];
            csIds.forEach((id) => {
              if (typeof id !== 'string') return;
              if (csOp === 'haveitem') assetIds.items.add(id);
              if (csOp === 'haverole') assetIds.titles.add(id);
            });
          }
        });
      };
      collectFromCompound(controlStructure.ifCompoundStatements);
      collectFromCompound(controlStructure.elseCompoundStatements);
    });
  };

  traverseSteps(algorithm.steps);
  return assetIds;
};

export const UpdateAlgorithmRefs = (
  algorithm: Algorithm | undefined,
  idMap: Map<string, string>
) => {
  if (!algorithm?.steps) return;

  const traverseSteps = (
    steps: Array<AlgorithmStep | AlgorithmControlStructure>
  ) => {
    steps.forEach((step) => {
      if (step.argumentAssetId && typeof step.argumentAssetId === 'string') {
        const newId = idMap.get(step.argumentAssetId);
        if (newId) step.argumentAssetId = newId;
      } else if (Array.isArray(step.argumentAssetId)) {
        step.argumentAssetId = step.argumentAssetId.map(
          (id) => idMap.get(id) || id
        );
      }

      const controlStructure = step as AlgorithmControlStructure;
      if (controlStructure.ifSteps) {
        traverseSteps(controlStructure.ifSteps);
      }
      if (controlStructure.elseSteps) {
        traverseSteps(controlStructure.elseSteps);
      }

      // Remap compound statement references
      const remapCompound = (
        statements?: Array<{ argumentAssetId?: string | string[] }>
      ) => {
        statements?.forEach((cs) => {
          if (cs.argumentAssetId && typeof cs.argumentAssetId === 'string') {
            const newId = idMap.get(cs.argumentAssetId);
            if (newId) cs.argumentAssetId = newId;
          } else if (Array.isArray(cs.argumentAssetId)) {
            cs.argumentAssetId = cs.argumentAssetId.map(
              (id) => idMap.get(id) || id
            );
          }
        });
      };
      remapCompound(controlStructure.ifCompoundStatements);
      remapCompound(controlStructure.elseCompoundStatements);
    });
  };

  traverseSteps(algorithm.steps);
};

/**
 * Regenerate all step IDs within an algorithm to prevent duplicate IDs on import.
 */
export const RegenerateAlgorithmStepIds = (algorithm?: Algorithm) => {
  if (!algorithm?.steps) return;

  const traverse = (
    steps: Array<AlgorithmStep | AlgorithmControlStructure>
  ) => {
    steps.forEach((step) => {
      step.id = uuid();
      const cs = step as AlgorithmControlStructure;
      if (cs.ifCompoundStatements) {
        cs.ifCompoundStatements.forEach((s) => {
          s.id = uuid();
        });
      }
      if (cs.elseCompoundStatements) {
        cs.elseCompoundStatements.forEach((s) => {
          s.id = uuid();
        });
      }
      if (cs.ifSteps) traverse(cs.ifSteps);
      if (cs.elseSteps) traverse(cs.elseSteps);
    });
  };

  traverse(algorithm.steps);
};

/**
 * Import the json formatted zones to the game document.
 * @param gameDocument - The Game Document to import to.
 * @param exportedTaskContentData - The exporte3d zzones data that would be imported
 */
export const ImportZones = async (
  gameDocument: GameDocument,
  exportedZoneData: ExportedZoneData
) => {
  const zones = gameDocument.assets.zones!;
  const maps = gameDocument.assets.maps!;
  const tasks = gameDocument.assets.tasks!;
  const areas = gameDocument.assets.areas!;
  const timers = gameDocument.assets.timers!;
  const taskContents = gameDocument.assets.taskContents!;
  const items = gameDocument.assets.items!;
  const titles = gameDocument.assets.titles!;
  const mapImages = gameDocument.assets.mapImages || [];
  if (!gameDocument.assets.mapImages) gameDocument.assets.mapImages = mapImages;

  const worldMapZones = gameDocument.rules.worldMap.zones;
  const exportedResources = exportedZoneData.resources;
  const exportedResourcePacks = exportedZoneData.resourcePacks;

  const idMap = new Map<string, string>();

  // 1. Import TaskContents
  if (exportedZoneData.taskContents) {
    for (const tc of exportedZoneData.taskContents) {
      const newTaskContent = _.cloneDeep(tc);
      const newId = uuid();
      idMap.set(tc.id, newId);
      newTaskContent.id = newId;

      newTaskContent.titleResId = await ImportResourceAsync(
        gameDocument,
        tc.titleResId,
        exportedResources,
        exportedResourcePacks
      );
      newTaskContent.preMessageResId = await ImportResourceAsync(
        gameDocument,
        tc.preMessageResId,
        exportedResources,
        exportedResourcePacks
      );
      newTaskContent.contentResId = await ImportResourceAsync(
        gameDocument,
        tc.contentResId,
        exportedResources,
        exportedResourcePacks
      );

      if (newTaskContent.forms) {
        for (const form of newTaskContent.forms) {
          form.id = uuid();
          form.questionResId = await ImportResourceAsync(
            gameDocument,
            form.questionResId,
            exportedResources,
            exportedResourcePacks
          );
          form.mediaOnlyIdentifierResId = await ImportResourceAsync(
            gameDocument,
            form.mediaOnlyIdentifierResId,
            exportedResources,
            exportedResourcePacks
          );
          if (form.customFeedback) {
            form.customFeedback.allCorrectMessageResId =
              await ImportResourceAsync(
                gameDocument,
                form.customFeedback.allCorrectMessageResId,
                exportedResources,
                exportedResourcePacks
              );
            form.customFeedback.partiallyCorrectMessageResId =
              await ImportResourceAsync(
                gameDocument,
                form.customFeedback.partiallyCorrectMessageResId,
                exportedResources,
                exportedResourcePacks
              );
            form.customFeedback.allWrongMessageResId =
              await ImportResourceAsync(
                gameDocument,
                form.customFeedback.allWrongMessageResId,
                exportedResources,
                exportedResourcePacks
              );
          }
          if (form.medias) {
            const medias = form.medias;
            form.medias = [];
            for (const m of medias) {
              const newMediaId = await ImportResourceAsync(
                gameDocument,
                m,
                exportedResources,
                exportedResourcePacks
              );
              if (newMediaId) form.medias.push(newMediaId);
            }
          }
          if (form.answers) {
            for (const a of form.answers) {
              const newAnswerResId = await ImportResourceAsync(
                gameDocument,
                a.answerResId,
                exportedResources,
                exportedResourcePacks
              );
              if (newAnswerResId) a.answerResId = newAnswerResId;
              a.id = uuid();
            }
          }
        }
      }
      taskContents.push(newTaskContent);
    }
  }

  // 2. Import Timers
  if (exportedZoneData.timers) {
    for (const timer of exportedZoneData.timers) {
      const newTimer = _.cloneDeep(timer);
      const newId = uuid();
      idMap.set(timer.id, newId);
      newTimer.id = newId;

      newTimer.titleResId = await ImportResourceAsync(
        gameDocument,
        timer.titleResId,
        exportedResources,
        exportedResourcePacks
      );

      // Regenerate algorithm step IDs for timer events
      RegenerateAlgorithmStepIds(newTimer.events?.onStartTimer);
      RegenerateAlgorithmStepIds(newTimer.events?.onStopTimer);
      RegenerateAlgorithmStepIds(newTimer.events?.onElapsedTimer);

      timers.push(newTimer);
    }
  }

  // 3. Import Items
  if (exportedZoneData.items) {
    for (const item of exportedZoneData.items) {
      const newItem = _.cloneDeep(item);
      const newId = uuid();
      idMap.set(item.id, newId);
      newItem.id = newId;

      newItem.titleResId = await ImportResourceAsync(
        gameDocument,
        item.titleResId,
        exportedResources,
        exportedResourcePacks
      );
      newItem.summaryResId = await ImportResourceAsync(
        gameDocument,
        item.summaryResId,
        exportedResources,
        exportedResourcePacks
      );
      newItem.imageResId = await ImportResourceAsync(
        gameDocument,
        item.imageResId,
        exportedResources,
        exportedResourcePacks
      );

      items.push(newItem);
    }
  }

  // 4. Import Titles
  if (exportedZoneData.titles) {
    for (const title of exportedZoneData.titles) {
      const newTitle = _.cloneDeep(title);
      const newId = uuid();
      idMap.set(title.id, newId);
      newTitle.id = newId;

      newTitle.titleResId = await ImportResourceAsync(
        gameDocument,
        title.titleResId,
        exportedResources,
        exportedResourcePacks
      );
      newTitle.summaryResId = await ImportResourceAsync(
        gameDocument,
        title.summaryResId,
        exportedResources,
        exportedResourcePacks
      );
      newTitle.imageResId = await ImportResourceAsync(
        gameDocument,
        title.imageResId,
        exportedResources,
        exportedResourcePacks
      );

      titles.push(newTitle);
    }
  }

  // 5. Import Tasks
  if (exportedZoneData.tasks) {
    for (const task of exportedZoneData.tasks) {
      const newTask = _.cloneDeep(task);
      const newId = uuid();
      idMap.set(task.id, newId);
      newTask.id = newId;

      if (newTask.boundary && newTask.boundary.id) {
        newTask.boundary.id = uuid();
      }

      newTask.titleResId = await ImportResourceAsync(
        gameDocument,
        task.titleResId,
        exportedResources,
        exportedResourcePacks
      );
      newTask.imageResId = await ImportResourceAsync(
        gameDocument,
        task.imageResId,
        exportedResources,
        exportedResourcePacks
      );
      newTask.completeImageResId = await ImportResourceAsync(
        gameDocument,
        task.completeImageResId,
        exportedResources,
        exportedResourcePacks
      );
      if (task.pin?.imageResId) {
        const newPinImageResId = await ImportResourceAsync(
          gameDocument,
          task.pin.imageResId,
          exportedResources,
          exportedResourcePacks
        );
        if (newPinImageResId && newTask.pin)
          newTask.pin.imageResId = newPinImageResId;
      }

      // Update refs
      if (task.taskContentId)
        newTask.taskContentId = idMap.get(task.taskContentId);
      if (task.timerAssId) newTask.timerAssId = idMap.get(task.timerAssId);

      // Regenerate algorithm step IDs for task events
      RegenerateAlgorithmStepIds(newTask.events?.onOpenTask);
      RegenerateAlgorithmStepIds(newTask.events?.onCompleteTask);
      RegenerateAlgorithmStepIds(newTask.events?.onCloseTask);
      RegenerateAlgorithmStepIds(newTask.events?.onProximity);

      tasks.push(newTask);
    }
  }

  // 6. Import Areas
  if (exportedZoneData.areas) {
    for (const area of exportedZoneData.areas) {
      const newArea = _.cloneDeep(area);
      const newId = uuid();
      idMap.set(area.id, newId);
      newArea.id = newId;

      if (newArea.boundary && newArea.boundary.id) {
        newArea.boundary.id = uuid();
      }

      newArea.titleResId = await ImportResourceAsync(
        gameDocument,
        area.titleResId,
        exportedResources,
        exportedResourcePacks
      );
      newArea.imageResId = await ImportResourceAsync(
        gameDocument,
        area.imageResId,
        exportedResources,
        exportedResourcePacks
      );

      // Regenerate algorithm step IDs for area events
      RegenerateAlgorithmStepIds(newArea.events?.onEntry);
      RegenerateAlgorithmStepIds(newArea.events?.onExit);

      areas.push(newArea);
    }
  }

  // 7. Import Maps
  if (exportedZoneData.maps) {
    for (const map of exportedZoneData.maps) {
      const newMap = _.cloneDeep(map);
      const newId = uuid();
      idMap.set(map.id, newId);
      newMap.id = newId;

      newMap.titleResId = await ImportResourceAsync(
        gameDocument,
        map.titleResId,
        exportedResources,
        exportedResourcePacks
      );
      newMap.imageResId = await ImportResourceAsync(
        gameDocument,
        map.imageResId,
        exportedResources,
        exportedResourcePacks
      );

      maps.push(newMap);
    }
  }

  // 7.5 Import MapImages
  if (exportedZoneData.mapImages) {
    for (const mi of exportedZoneData.mapImages) {
      const newMi = _.cloneDeep(mi);
      const newId = uuid();
      idMap.set(mi.id, newId);
      newMi.id = newId;

      newMi.titleResId = await ImportResourceAsync(
        gameDocument,
        mi.titleResId,
        exportedResources,
        exportedResourcePacks
      );
      newMi.imageResId = await ImportResourceAsync(
        gameDocument,
        mi.imageResId,
        exportedResources,
        exportedResourcePacks
      );

      // Remap mapId
      if (mi.mapId) {
        newMi.mapId = idMap.get(mi.mapId) || mi.mapId;
      }

      mapImages.push(newMi);
    }
  }

  // 8. Import Zones
  for (const zone of exportedZoneData.zones) {
    const newZone = _.cloneDeep(zone);
    const newId = uuid();
    idMap.set(zone.id, newId);
    newZone.id = newId;

    if (newZone.boundary && newZone.boundary.id) {
      newZone.boundary.id = uuid();
    }

    newZone.titleResId = await ImportResourceAsync(
      gameDocument,
      zone.titleResId,
      exportedResources,
      exportedResourcePacks
    );
    newZone.imageResId = await ImportResourceAsync(
      gameDocument,
      zone.imageResId,
      exportedResources,
      exportedResourcePacks
    );

    // Update refs
    if (newZone.tasks) {
      newZone.tasks.forEach((t) => {
        t.taskAssId = idMap.get(t.taskAssId) || t.taskAssId;
      });
    }
    if (newZone.areas) {
      newZone.areas.forEach((a) => {
        a.areaAssId = idMap.get(a.areaAssId) || a.areaAssId;
      });
    }
    if (newZone.timers) {
      newZone.timers.forEach((t) => {
        t.timerAssId = idMap.get(t.timerAssId) || t.timerAssId;
      });
    }

    // Regenerate algorithm step IDs for zone events
    RegenerateAlgorithmStepIds(newZone.events?.onEnterMap);
    RegenerateAlgorithmStepIds(newZone.events?.onExitMap);
    RegenerateAlgorithmStepIds(newZone.events?.onTaskCompletedMap);

    zones.push(newZone);
  }

  // 9. Update Algorithm references in all imported assets
  const updateAllAlgorithms = () => {
    // Zones
    zones.forEach((z) => {
      if (z.events) {
        UpdateAlgorithmRefs(z.events.onEnterMap, idMap);
        UpdateAlgorithmRefs(z.events.onExitMap, idMap);
        UpdateAlgorithmRefs(z.events.onTaskCompletedMap, idMap);
      }
    });

    // Tasks
    tasks.forEach((t) => {
      if (t.events) {
        UpdateAlgorithmRefs(t.events.onOpenTask, idMap);
        UpdateAlgorithmRefs(t.events.onCompleteTask, idMap);
        UpdateAlgorithmRefs(t.events.onCloseTask, idMap);
        UpdateAlgorithmRefs(t.events.onProximity, idMap);
      }
    });

    // Areas
    areas.forEach((a) => {
      if (a.events) {
        UpdateAlgorithmRefs(a.events.onEntry, idMap);
        UpdateAlgorithmRefs(a.events.onExit, idMap);
      }
    });

    // Timers
    timers.forEach((t) => {
      if (t.events) {
        UpdateAlgorithmRefs(t.events.onStartTimer, idMap);
        UpdateAlgorithmRefs(t.events.onStopTimer, idMap);
        UpdateAlgorithmRefs(t.events.onElapsedTimer, idMap);
      }
    });

    // World Map events
    if (gameDocument.rules?.worldMap?.events) {
      UpdateAlgorithmRefs(gameDocument.rules.worldMap.events.onEnterMap, idMap);
      UpdateAlgorithmRefs(gameDocument.rules.worldMap.events.onExitMap, idMap);
      UpdateAlgorithmRefs(
        gameDocument.rules.worldMap.events.onTaskCompletedMap,
        idMap
      );
    }
  };

  updateAllAlgorithms();

  // 10. Import WorldMapZones
  for (const wmz of exportedZoneData.worldMapZones) {
    const newWmz = _.cloneDeep(wmz);
    newWmz.zoneAssId = idMap.get(wmz.zoneAssId) || wmz.zoneAssId;
    if (wmz.mapAssId) newWmz.mapAssId = idMap.get(wmz.mapAssId) || wmz.mapAssId;
    if (wmz.timerAssId)
      newWmz.timerAssId = idMap.get(wmz.timerAssId) || wmz.timerAssId;

    worldMapZones!.push(newWmz);
  }

  return MergeAssets(gameDocument, zones!, 'zones');
};
